import type { LocationTypeInfo } from '@/domain/cages';
import { canonicalTimezoneName } from '@/utils/date/timezoneNames';

/** What the modal hands its caller once the form is submitted. */
export interface AddNewLocationInput {
    name: string;
    timezone: string;
    aquacultureSiteId?: number;
    latitude?: number;
    longitude?: number;
    id?: number;
    locationtypeid: number;
    /** Null for a location without a default species. */
    fishtypeid: number | null;
    licetypeid: number;
    location_type?: LocationTypeInfo;
}

/** The location being edited, as the modal receives it. Its species can be unset. */
export type LocationFormInitialState = AddNewLocationInput;

/**
 * The form's own values. Text inputs and selects hold strings, and an untouched optional
 * field is null; the numbers only appear when `fromLocationFormValues` converts on submit.
 */
export interface LocationFormValues {
    id?: number;
    name: string;
    timezone: string;
    aquacultureSiteId: string | null;
    latitude: string | null;
    longitude: string | null;
    locationtypeid: string;
    fishtypeid: string;
    licetypeid: string;
}

/** The option value the selects show before anything is chosen. */
export const UNSELECTED_OPTION = '-1';

/** The species option of an existing location without a default species. */
export const NO_FISH_TYPE_OPTION = '';

/** Whether a select holds a choice rather than its placeholder. */
export function isOptionChosen(value: string): boolean {
    return value !== UNSELECTED_OPTION;
}

/**
 * Whether the species select holds an acceptable choice. "No default species" is one only
 * when editing a location that has none: saving it keeps the species unset instead of forcing
 * one on every edit, while a new location must still name the species its pens start with.
 */
export function isFishTypeChosen(value: string, allowsNoFishType: boolean): boolean {
    return isOptionChosen(value) && (allowsNoFishType || value !== NO_FISH_TYPE_OPTION);
}

export function toFieldString(value: number | null | undefined): string | null {
    return value == null ? null : String(value);
}

export function toLocationFormValues(initialState?: LocationFormInitialState): LocationFormValues {
    if (!initialState) {
        return {
            name: '',
            timezone: 'Europe/Oslo',
            aquacultureSiteId: null,
            latitude: null,
            longitude: null,
            locationtypeid: UNSELECTED_OPTION,
            fishtypeid: UNSELECTED_OPTION,
            licetypeid: UNSELECTED_OPTION,
        };
    }
    return {
        id: initialState.id,
        name: initialState.name,
        // The zone a save stores, so a legacy alias shows as the zone it names.
        timezone: canonicalTimezoneName(initialState.timezone) ?? initialState.timezone,
        aquacultureSiteId: toFieldString(initialState.aquacultureSiteId),
        latitude: toFieldString(initialState.latitude),
        longitude: toFieldString(initialState.longitude),
        locationtypeid: String(initialState.location_type?.id ?? 1),
        fishtypeid: toFieldString(initialState.fishtypeid) ?? NO_FISH_TYPE_OPTION,
        licetypeid: String(initialState.licetypeid),
    };
}

function toOptionalInt(value: string | null): number | undefined {
    return value ? Number.parseInt(value, 10) : undefined;
}

function toOptionalCoordinate(value: string | null): number | undefined {
    if (!value) {
        return undefined;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
}

export function fromLocationFormValues(values: LocationFormValues): AddNewLocationInput {
    return {
        id: values.id,
        name: values.name,
        timezone: values.timezone,
        aquacultureSiteId: toOptionalInt(values.aquacultureSiteId),
        latitude: toOptionalCoordinate(values.latitude),
        longitude: toOptionalCoordinate(values.longitude),
        locationtypeid: Number.parseInt(values.locationtypeid, 10),
        // "No default species" keeps the location without one; parsing it gave NaN.
        fishtypeid:
            values.fishtypeid === NO_FISH_TYPE_OPTION
                ? null
                : Number.parseInt(values.fishtypeid, 10),
        licetypeid: Number.parseInt(values.licetypeid, 10),
    };
}
